import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { OrdersDataViewContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import reorder from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import updateSearchFields from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import { OrderHistoryPageContext } from "@insite/content-library/Pages/OrderHistoryPage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { ClickableProps } from "@insite/mobius/Clickable";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import Icon from "@insite/mobius/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import HistoryContext, { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showReorderProducts = "showReorderProducts",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showReorderProducts]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        parameter: state.pages.orderHistory.getOrdersParameter,
        isReordering: state.pages.orderHistory.isReordering,
        showAddToCartConfirmationDialog: getSettingsCollection(state).productSettings.showAddToCartConfirmationDialog,
        language: state.context.session.language,
        showPoNumber: getSettingsCollection(state).orderSettings.showPoNumber,
        orderDetailsPageLink: getPageLinkByPageType(state, "OrderDetailsPage"),
    };
};

const mapDispatchToProps = {
    updateSearchFields,
    reorder,
};

type Props = ReturnType<typeof mapStateToProps> &
    OwnProps &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface OrderHistoryTableStyles {
    container?: InjectableCss;
    headerClickable?: ClickableProps;
    headerText?: TypographyProps;
    centeringWrapper?: InjectableCss;
    centeringLoaderWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    TableTr?: InjectableCss;
    noResultsText?: TypographyProps;
    orderDateHeader?: DataTableHeaderProps;
    orderNumberHeader?: DataTableHeaderProps;
    shipToHeader?: DataTableHeaderProps;
    statusHeader?: DataTableHeaderProps;
    confirmationNumberHeader?: DataTableHeaderProps;
    customerPOHeader?: DataTableHeaderProps;
    orderTotalHeader?: DataTableHeaderProps;
    reorderHeader?: DataTableHeaderProps;
    orderDateCells?: DataTableCellProps;
    orderNumberCells?: DataTableCellProps;
    confirmationOrderNumberCells?: DataTableCellProps;
    shipToCells?: DataTableCellProps;
    statusCells?: DataTableCellProps;
    customerPOCells?: DataTableCellProps;
    orderTotalCells?: DataTableCellProps;
    reorderCells?: DataTableCellProps;
    reorderButton?: ButtonPresentationProps;
    reorderButtonSpinner?: LoadingSpinnerProps;
    orderNumberLink?: LinkPresentationProps;
    dataTable?: DataTableProps;
    orderView?: InjectableCss;
    mobileHeading?: InjectableCss;
}

export const orderHistoryTableStyles: OrderHistoryTableStyles = {
    container: {
        css: css`
            margin-top: -10px;
            position: relative;

            @media screen and (max-width: 767px) {
                margin-bottom: 15px;
                margin-top: 10px;
                overflow: hidden;
            }
        `,
    },
    centeringWrapper: {
        css: css`
            height: 18.75rem;
            display: flex;
            align-items: center;
        `,
    },
    centeringLoaderWrapper:{
        css: css`
            position: fixed;
            background: rgba(0, 37, 84, 0.2);
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            z-index: 99;
            top: 0;
            left: 0;

            svg {
                position: relative;
                z-index: 99;
            }
        `,
    },
    TableTr: {
        css: css`
            &:nth-child(even) {
                background: #fff;
            }
            @media screen and (min-width: 1280px) {
                box-shadow: 0 2px 12px 0 rgba(0,37,84,0.04);
                td {
                    border-bottom: none;
                }
            }
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 1.25rem;
        `,
    },
    noResultsText: {
        variant: "h4",
    },
    headerClickable: {
        css: css`
            width: 100%;
            justify-content: space-between;
        `,
    },
    reorderButton: {
        color: "secondary",
        sizeVariant: "medium",
    },
    reorderButtonSpinner: {
        size: 22,
        css: css`
            margin-left: auto;
            margin-right: auto;
            display: block;
        `,
    },
    shipToHeader: {
        tight: true,
    },
    orderTotalHeader: {
        tight: true,
        alignX: "right",
    },
    reorderHeader: {
        tight: true,
    },
    orderTotalCells: {
        alignX: "right",
        css: css`
            font-weight: bold;

            @media screen and (max-width: 1279px) {
                padding: 27px 0;
                .total-value {
                    font-size: 16px;
                }
            }
        `,
    },
    shipToCells: {
        typographyProps: {
            ellipsis: true,
            css: css`
                display: block;
                max-width: 15.75rem;

                span:last-child {
                    text-transform: capitalize;
                }
            `,
        },
        css: css`
            min-width: 1rem;
            white-space: normal;

            @media screen and (min-width: 1279px) {
                max-width: 7.75rem;
                min-width: 6rem;
            }

        `,
    },
    dataTable: {
        css: css`
            border-spacing: 0 15px;
            background: #f7f9fa;
            border-collapse: separate;
            th {
                border-bottom: 0;
                color: #2060AD;
                font-family: 'Roboto', sans-serif;
                font-size: 14px;
                font-weight: bold;
                letter-spacing: .81px;
                line-height: 16px;

                span {
                    font-size: 14px;
                }

                &:first-child {
                    font-weight: normal;
                    letter-spacing: 0;

                    span {
                        font-weight: normal;
                        letter-spacing: 0;
                    }
                }
                
                button {
                    color: #2060AD;
                    font-family: 'Roboto', sans-serif;
                    font-size: 14px;
                    font-weight: bold;
                    letter-spacing: .81px;
                    line-height: 16px;
                    width: auto;  
                }

                svg {
                    color: #2E5BFF;
                }
            }
            td {
                color: #2E384D;
                font-family: 'Lato',sans-serif;
                font-size: 14px;
                letter-spacing: 0;
                line-height: 22px;

                span, a {
                    color: #2E384D !important;
                    font-family: 'Lato',sans-serif;
                    font-size: 14px;
                }
            }

            @media screen and (min-width: 1280px) {
                thead {
                    position: relative;
                    top: 14px;
                    border-radius: 0 0 2px 2px;
                    
                }
                th {
                    height: 40px;
                    background-color: #E5EEF7;
                    width: auto;
                    text-align: left;

                    &:first-child {
                        padding-left: 30px;
                    }
                    &:last-child {
                        padding-right: 30px;
                        min-width: 5.625rem;
                        text-align: right;
                    }
                }
                td {
                    height: 60px;

                    &:first-child {
                        padding-left: 30px;
                    }
                    &:last-child {
                        padding-right: 30px;
                        min-width: 5.625rem;
                    }
                    span.shipping-value {
                        text-transform: capitalize;
                    }
                }

                tbody {
                    th {
                        display: none;
                    }
                }
            }

            @media screen and (min-width: 1300px) {
                th {
                    &:last-child {
                        min-width: 9.625rem;
                    }
                }
                td {
                    &:last-child {
                        min-width: 9.625rem;
                    }
                }
            }

            @media screen and (max-width: 1279px) {
                thead {
                    display: none;
                }

                table,
                tbody,
                tr{
                    display: flex;
                    max-width: 100%;
                    flex-direction: column;

                    td[data-test-selector="orderHistoryTable_tableCell_date"] {
                        display: none;
                    }
                }

                .mob-date-value {
                    position: absolute;
                    right: 40px;
                    color: #2E384D;
                    font-family: Lato;
                    font-size: 14px;
                    letter-spacing: 0;
                    line-height: 22px;
                    text-align: right;
                    font-weight: normal;
                }

                th {
                    background: transparent !important;
                    height: auto !important;
                    border-bottom: 1px solid #D7D7D7 !important;
                    line-height: 24px !important;
                    padding: 4px 10px !important;
                    margin: 0 15px;
                }

                tr {
                    margin-bottom: 10px;
                    
                    &:before {
                        height: 27px;
                        width: 100%;
                        border-radius: 0 0 2px 2px;
                        background-color: #E5EEF7;
                        content: '';
                    }
                }

                td {
                    display: flex;
                    align-content: center;
                    height: auto;
                    border-color: #D7D7D7;
                    line-height: 24px;
                    padding: 4px 10px;
                    margin: 0 15px;
                    justify-content: space-between;

                    &.total-container{
                        padding: 27px 10px;
                        margin: 0 15px;
                        border: none;

                        span:last-child {
                            font-weight: bold;
                        }
                    }

                    &:first-child {
                        font-weight: normal;
                        letter-spacing: 0;
                        padding: 11px 0 0 10px;
                        
                        span {
                            font-weight: normal;
                            letter-spacing: 0;
                        }
                    }

                    > span {
                        display: inline-flex;
                        align-items: center;
                        max-width: 49%;
                        width: 49%;
                        text-align: right;
                        white-space: normal;
                        justify-content: flex-end;

                        &:first-child {
                            justify-content: flex-start;
                        }

                        &.shipping-value {
                            text-transform: capitalize;
                        }

                        &:last-child {
                            justify-content: flex-end;
                            color: #2E384D;
                            font-family: Lato;
                            font-size: 14px;
                            letter-spacing: 0;
                            line-height: 22px;
                            text-align: right;
                            font-weight: normal;
                        }
                    }

                    a {
                        text-align: right;
                        color: #2E384D !important;
                        font-family: Lato;
                        font-size: 14px;
                        letter-spacing: 0;
                        line-height: 22px;
                        text-align: right;
                        font-weight: normal;

                        span {
                            color: #2E384D !important;
                        }

                    }
                }

            }
        `,
    },
    orderView: {
        css: css`
            padding: 0 .938rem;
            position: relative;
            text-align: center;
            align-items: center;
            justify-content: center;

            @media screen and (min-width: 1280px) {
                text-align: -webkit-left;
                justify-content: flex-start;
                margin-left: 0 !important;
                padding-left: 0 !important;
                
            }

            &:after {
                content: '';
                display: block;
                height: 1.875rem;
                width: .063rem;
                position: absolute;
                right: -.938rem;
                border-right: .063rem solid #E0E0E0;
            }

            @media screen and (min-width: 1280px) {
                &:after {
                    right: auto;
                    left: 2.5rem;
                }
            }

            @media screen and (max-width: 1279px) {
                &:after {
                    content: none;
                }
            }
        `,
    },
    mobileHeading: {
        css: css`
            @media screen and (min-width: 1280px) {
                display: none;
            }

            @media screen and (max-width: 1279px) {
                background: none;
                display: inline-flex;
                width: 49%;
                color: #2060AD;
                font-family: 'Roboto', sans-serif;
                font-size: 14px;
                font-weight: bold;
                letter-spacing: .81px;
                line-height: 16px;
                align-items: center;
                text-align: left;
                padding: 0;
                height: auto;

                button {
                    span {
                        font-weight: bold !important;
                        font-size: 14px;
                    }
                }
            }
        `,
    },
};

const styles = orderHistoryTableStyles;

class OrderHistoryTable extends React.Component<Props> {
    static contextType = OrdersDataViewContext;
    context!: React.ContextType<typeof OrdersDataViewContext>;

    state = {
        isLoadingNew: false,
    };

    handleLinkClick = () => {
        this.setState({ isLoadingNew: true });
    }

    headerClick(sortField: string) {
        const sort = this.props.parameter.sort === sortField ? `${sortField} DESC` : sortField;
        this.props.updateSearchFields({ sort });
    }

    sorted = (sortField: string) => {
        let sorted: boolean | string = false;
        if (this.props.parameter.sort === sortField) {
            sorted = "ascending";
        } else if (this.props.parameter.sort === `${sortField} DESC`) {
            sorted = "descending";
        }
        return sorted as SortOrderOptions;
    };

    onReorderSuccess = (orderNumber: string, linkOrderNumber: string) => {
        if (!this.props.showAddToCartConfirmationDialog) {
            return;
        }
        this.props.toaster.addToast({
            body: (
                <HistoryContext.Provider value={{ history: this.props.history }}>
                    <OrderDetailPageTypeLink title={orderNumber} orderNumber={linkOrderNumber} />
                    &nbsp;{translate("Added to Cart")}
                </HistoryContext.Provider>
            ),
            messageType: "success",
            timeoutLength: 6000,
        });
    };

    render() {
        const { isLoadingNew } = this.state;
        const ordersDataView = this.context;
        if (ordersDataView.isLoading) {
            return (
                <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            );
        }

        if (!ordersDataView.value) {
            return null;
        }

        if (ordersDataView.value.length === 0) {
            return (
                <StyledWrapper {...styles.noResultsContainer}>
                    <Typography as="p" {...styles.noResultsText} data-test-selector="orderHistoryTable_noOrdersFound">
                        {translate("No orders found")}
                    </Typography>
                </StyledWrapper>
            );
        }



        const rows = ordersDataView.value.map(order => {

            return {
                id: order.id,
                linkOrderNumber: order.webOrderNumber ?? "",
                date: order.orderDate
                    ? getLocalizedDateTime({
                        dateTime: new Date(order.orderDate),
                        language: this.props.language,
                    })
                    : "",
                orderNumber: order.webOrderNumber || " ",
                shipTo: `${order.stCompanyName}`,
                status: order.statusDisplay,
                po: order.customerPO,
                total: order.orderGrandTotalDisplay,
                orderChannel: order.properties?.orderChannel,
                erpOrderNumber: order.erpOrderNumber || " ",
                linkErpOrderNumber: order.erpOrderNumber || " ",
            };
        });

        return (
            <StyledWrapper {...styles.container} data-test-selector="orderHistoryTable">
                {isLoadingNew && (
                    <StyledWrapper {...styles.centeringLoaderWrapper}>
                        <LoadingSpinner {...styles.spinner} />
                    </StyledWrapper>
                )}
                <DataTable {...styles.dataTable}>
                    <DataTableHead>
                        <DataTableHeader
                            {...styles.orderDateHeader}
                        >
                            {translate("View", "View")}
                        </DataTableHeader>
                        <DataTableHeader
                            {...styles.orderDateHeader}
                            sorted={this.sorted("orderDate")}
                            onSortClick={() => this.headerClick("orderDate")}
                        >
                            {translate("Order Date", "orderDate")}
                        </DataTableHeader>
                        <DataTableHeader
                            {...styles.orderNumberHeader}
                            title={translate("Order Number")}
                        >
                            {translate("Order #", "webOrderNumber")}
                        </DataTableHeader>
                        <DataTableHeader
                            {...styles.statusHeader}
                        >
                            {translate("Status", "status")}
                        </DataTableHeader>
                        <DataTableHeader
                            {...styles.confirmationNumberHeader}
                        >
                            {translate("Web Order", "webOrder")}
                        </DataTableHeader>
                        <DataTableHeader {...styles.orderTotalHeader}>
                            {translate("Order Channel", "orderChannel")}
                        </DataTableHeader>
                        <DataTableHeader
                            {...styles.shipToHeader}
                        >
                            {translate("Ship to ", "stCompanyName")}
                        </DataTableHeader>

                        {this.props.showPoNumber && (
                            <DataTableHeader
                                {...styles.customerPOHeader}
                                title={translate("Purchase Order Number")}
                            >
                                {translate("PO #", "customerPO")}
                            </DataTableHeader>
                        )}
                        <DataTableHeader
                            {...styles.orderTotalHeader}
                        >
                            {translate("Total", "orderTotal")}
                        </DataTableHeader>
                    </DataTableHead>
                    <DataTableBody data-test-selector="orderHistoryTable_tableBody">
                        {rows.map(
                            ({ id, linkOrderNumber, date, orderNumber, shipTo, status, po, total, orderChannel, erpOrderNumber, linkErpOrderNumber }) => (
                                <DataTableRow {...styles.TableTr} key={id}>
                                    <DataTableCell>
                                        <Typography {...styles.mobileHeading}>
                                            {translate("View", "View")}
                                        </Typography>
                                        {orderNumber !== " " ? (
                                            <Link onClick={this.handleLinkClick} href={`${this.props.orderDetailsPageLink?.url}?orderNumber=${orderNumber}`}  {...styles.orderView}>
                                                <Icon src="Eye" size={24} />
                                            </Link>
                                        ) :
                                            <Link onClick={this.handleLinkClick} href={`${this.props.orderDetailsPageLink?.url}?orderNumber=${erpOrderNumber}`}  {...styles.orderView}>
                                                <Icon src="Eye" size={24} />
                                            </Link>
                                        }

                                    </DataTableCell>
                                    <DataTableHeader
                                        {...styles.statusCells}
                                        sorted={this.sorted("orderDate")}
                                        onSortClick={() => this.headerClick("orderDate")}
                                    >
                                        {translate("Order Date", "orderDate")}
                                        <Typography className="mob-date-value">{date}</Typography>
                                    </DataTableHeader>
                                    <DataTableCell
                                        {...styles.orderDateCells}
                                        data-test-selector="orderHistoryTable_tableCell_date"
                                    >
                                        <Typography>{date}</Typography>
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.orderNumberCells}
                                        data-test-selector="orderHistoryTable_tableCell_orderNumber"
                                    >
                                        <Typography {...styles.mobileHeading}>
                                            {translate("Order #", "webOrderNumber")}
                                        </Typography>
                                        <OrderDetailPageTypeLink title={erpOrderNumber} orderNumber={linkErpOrderNumber} />
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.statusCells}
                                        data-test-selector="orderHistoryTable_tableCell_status"
                                    >
                                        <Typography {...styles.mobileHeading}>
                                            {translate("Status", "status")}
                                        </Typography>
                                        <Typography>{status}</Typography>
                                    </DataTableCell>

                                    <DataTableCell
                                        {...styles.confirmationOrderNumberCells}
                                        data-test-selector="orderHistoryTable_tableCell_confirmationOrderNumber"
                                    >
                                        <Typography {...styles.mobileHeading}>
                                            {translate("Web Order", "webOrder")}
                                        </Typography>
                                        <OrderDetailPageTypeLink title={orderNumber} orderNumber={linkOrderNumber} />
                                    </DataTableCell>
                                    <DataTableCell {...styles.customerPOCells}>
                                        <Typography {...styles.mobileHeading}>
                                            {translate("Order Channel", "orderChannel")}
                                        </Typography>
                                        <Typography>{orderChannel}</Typography>

                                    </DataTableCell>
                                    <DataTableCell {...styles.shipToCells}>
                                        <Typography {...styles.mobileHeading}>
                                            {translate("Ship to ", "stCompanyName")}
                                        </Typography>
                                        <Typography className="shipping-value">{shipTo.toLowerCase()}</Typography>
                                    </DataTableCell>

                                    {
                                        this.props.showPoNumber && (
                                            <DataTableCell {...styles.customerPOCells}>
                                                <Typography {...styles.mobileHeading}>
                                                    {translate("PO #", "customerPO")}
                                                </Typography>
                                                <Typography>{po}</Typography>

                                            </DataTableCell>
                                        )
                                    }
                                    <DataTableCell {...styles.orderTotalCells} className="total-container">
                                        <Typography {...styles.mobileHeading} className="total-label">
                                            {translate("Total ", "Total ")}
                                        </Typography>
                                        <Typography className="total-value">{total}</Typography>

                                    </DataTableCell>
                                </DataTableRow >
                            ),
                        )
                        }
                    </DataTableBody>
                </DataTable >
            </StyledWrapper >
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(OrderHistoryTable))),
    definition: {
        group: "Order History",
        displayName: "Search Results Table",
        allowedContexts: [OrderHistoryPageContext],
        fieldDefinitions: [
            {
                name: fields.showReorderProducts,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;